import { HttpService, RootContextBase, TaggedUrl, Tags } from '@skylab-shared/core';
import { Announcement, AnnouncementContentKind, AnnouncementKind } from '@skylab-shared/dataModel';
import { last } from '@skylab-shared/utils';
import { CompanyParametersQuery } from '../../../company/queries/company-parameters.query-base';
import { OnlineReceiversItem, OnlineReceiversQuery } from '../../../online-receivers/online-receivers.query-base';
import { ViewSettingsRepositoryBase } from '../../../view-settings/view-settings.repo-base';
import { EventNotification, EventNotificationsServiceBase, EventNotificationType } from '../../event-notifications.service-base';
import { NotificatorBase } from '../notificator-base';

export class AnnouncementsNotificatorBase extends NotificatorBase<Announcement[]> {
    protected readonly eventType: EventNotificationType = 'NotificationAdded';
    private readonly url = TaggedUrl.join('user/notifications', [Tags.Api, Tags.OmitMessage]);

    private announcements: Announcement[] = [];

    public constructor(
        private readonly rootContext: RootContextBase,
        private readonly viewSettingsRepository: ViewSettingsRepositoryBase,
        eventNotifications: EventNotificationsServiceBase,
        http: HttpService,
    ) {
        super(eventNotifications, http);
    }

    public async refreshAsync(): Promise<void> {
        const data = await this.prepareDataAsync();
        this.notifyObservers(data);
    }

    public getAnnouncements(): Announcement[] {
        return [...this.announcements];
    }

    public async loadAnnouncementsAsync(): Promise<void> {
        const { data: announcements } = await this.http.get(this.url);
        await this.manageAnnouncementsAsync(announcements);
    }

    protected async prepareDataAsync(_: EventNotification[] | null = null): Promise<Announcement[]> {
        await this.loadAnnouncementsAsync();
        return [...this.announcements];
    }

    public async setAsReadAsync(announcement: Pick<Announcement, 'id' | 'contentType'>): Promise<void> {
        if (announcement.id) {
            await this.http.delete(`${this.url}/${announcement.id}`);
        } else if (announcement.contentType === AnnouncementContentKind.NewOnlineReceiverAvailable) {
            this.setOnlineReceiverAnnouncementAsReadAsync();
        }

        this.refreshAsync();
    }

    public async deleteAllNotificationsAsync(): Promise<void> {
        await this.http.delete(`${this.url}/delete-all`);

        if (this.newOnlineReceiver) {
            this.setOnlineReceiverAnnouncementAsReadAsync();
        }

        this.refreshAsync();
    }

    //#region  MANAGE ANNOUNCEMENTS
    private newOnlineReceiver: OnlineReceiversItem | null;

    public get newOnlineReceiverAnnouncementChannelId() { return this.newOnlineReceiver?.channelId };
    public async setOnlineReceiverAnnouncementAsReadAsync(): Promise<void> {
        await this.viewSettingsRepository.saveViewSettingsAsync('last-ignored-new-online-reciever-announcement', this.newOnlineReceiverAnnouncementChannelId);
    }

    private async manageAnnouncementsAsync(announcements: Announcement[]): Promise<void> {
        this.newOnlineReceiver = await this.isOnlineReceiversAnnouncementRequired();
        if (this.newOnlineReceiver) announcements.push(this.onlineReceiverAnnouncement);

        const newAnnouncements = this.announcementsExcept(announcements, this.announcements);
        const removedAnnouncements = this.announcementsExcept(this.announcements, announcements);

        this.announcements = this.announcementsExcept(this.announcements, removedAnnouncements);
        this.announcements.push(...newAnnouncements);
    }

    private async isOnlineReceiversAnnouncementRequired(): Promise<OnlineReceiversItem | null> {
        const onlineReceivers = await this.rootContext.getAsync(OnlineReceiversQuery);
        if (!onlineReceivers?.length) return null;

        const { accountingOnlineReceiver } = await this.rootContext.getAsync(CompanyParametersQuery);
        const lastOnlineReciever = last(onlineReceivers.sort((elem1, elem2) => Number(elem1.channelId) - Number(elem2.channelId)));
        const lastSavedOnlineRecieverChannelId = await this.viewSettingsRepository.getViewSettingsAsync<string>('last-ignored-new-online-reciever-announcement');

        const newReceiver = accountingOnlineReceiver
            ? Number(accountingOnlineReceiver.channelId) !== Number(lastOnlineReciever.channelId) ? lastOnlineReciever : null
            : lastOnlineReciever;

        return newReceiver && newReceiver.channelId !== lastSavedOnlineRecieverChannelId ? newReceiver : null;
    }

    private get onlineReceiverAnnouncement(): Announcement {
        return {
            content: `{"Kind":"${AnnouncementContentKind.NewOnlineReceiverAvailable}"}`,
            kind: AnnouncementKind.Info,
            id: null,
            isDisplayedAsToast: false
        };
    }

    private announcementsExcept(array1: Announcement[], array2: Announcement[]): Announcement[] {
        return array1.filter(el1 => !array2.some(el2 => el1.id === el2.id));
    }
    //#endregion  MANAGE ANNOUNCEMENTS
}
